import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  rectIntersection,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

SortableItem.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const DroppableColumn = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column ${isOver ? "kanban-column-hover" : ""}`}
    >
      {children}
    </div>
  );
};

DroppableColumn.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const KanbanBoard = ({ columns, onDragEnd }) => {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) {
      return;
    }

    if (active.id !== over.id) {
      let sourceColumnId = null;
      let destinationColumnId = null;

      Object.entries(columns).forEach(([columnId, column]) => {
        const itemIndex = column.items.findIndex(
          (item) => item.id === active.id
        );
        if (itemIndex !== -1) {
          sourceColumnId = columnId;
        }
      });

      if (columns[over.id]) {
        destinationColumnId = over.id;
      } else {
        Object.entries(columns).forEach(([columnId, column]) => {
          const itemIndex = column.items.findIndex(
            (item) => item.id === over.id
          );
          if (itemIndex !== -1) {
            destinationColumnId = columnId;
          }
        });
      }

      if (sourceColumnId && destinationColumnId) {
        const sourceIndex = columns[sourceColumnId].items.findIndex(
          (item) => item.id === active.id
        );

        let destinationIndex;
        if (sourceColumnId === destinationColumnId) {
          destinationIndex = columns[destinationColumnId].items.findIndex(
            (item) => item.id === over.id
          );
        } else {
          if (columns[over.id]) {
            destinationIndex = columns[destinationColumnId].items.length;
          } else {
            destinationIndex = columns[destinationColumnId].items.findIndex(
              (item) => item.id === over.id
            );
          }
        }

        onDragEnd({
          draggableId: active.id,
          source: { droppableId: sourceColumnId, index: sourceIndex },
          destination: {
            droppableId: destinationColumnId,
            index: destinationIndex,
          },
        });
      }
    }
  };

  const getActiveItem = () => {
    if (!activeId) return null;

    for (const column of Object.values(columns)) {
      const item = column.items.find((item) => item.id === activeId);
      if (item) return item;
    }
    return null;
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div
        className="kanban-board"
        style={{
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <div
          className="kanban-columns"
          style={{
            display: "flex",
            gap: "1rem",
            paddingBottom: "1rem",
            width: "max-content",
            minWidth: "100%",
          }}
        >
          {Object.values(columns).map((column) => (
            <DroppableColumn key={column.id} id={column.id}>
              <div
                className="kanban-column-header"
                style={column.color ? { backgroundColor: column.color } : {}}
              >
                <h3>{column.title}</h3>
                <span className="task-count">{column.items.length}</span>
              </div>
              <div className="kanban-column-content">
                <SortableContext
                  items={column.items.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {column.items.map((item) => (
                    <SortableItem key={item.id} id={item.id}>
                      {item.content}
                    </SortableItem>
                  ))}
                </SortableContext>
              </div>
            </DroppableColumn>
          ))}
        </div>
      </div>
      <DragOverlay>
        {activeId ? (
          <div
            className="task-card"
            style={{ opacity: 0.8, transform: "rotate(5deg)" }}
          >
            {getActiveItem()?.content}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

KanbanBoard.propTypes = {
  columns: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          content: PropTypes.node.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  onDragEnd: PropTypes.func.isRequired,
};

export default KanbanBoard;
